import UserWithAvatar from "components/Atoms/UserWithAvatar";
import BookkeepingPaginatedDropdown from "components/dropdowns/paginated/BookkeepingPaginatedDropdown";
import BrandDropdown from "components/dropdowns/reactselect/BrandDropdown";
import { CurrencyAndExchangeRate, CurrencyAndExchangeRatePicker, ICurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import { SectionTemplateBrField } from "components/MainSectionTemplate/SectionTemplateBrField";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { BookkeepingSmallDetails } from "components/SmallDetails";
import SmallDetails from "components/SmallDetails/SmallDetails";
import LifecycleSteps from "components/Steps/LifecycleSteps";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import { EsignDocumentInfo } from "eSign/panels/DraftPanel";
import moment from "moment";
import React from "react";
import { formatDaysSince, formatWrittenDayMonthYear } from "utilities";

interface EsignDetailSectionProps {
  documentType: server.dto.DocumentTypes
  document: server.dto.ESignDocument
  onReviewClickValidate: () => boolean | Promise<boolean>
  hasErrors: boolean
  brands: server.dto.BrandSmall[]
  formState: EsignDocumentInfo
}

export const EsignDraftSection = (props: EsignDetailSectionProps): Section => {

  const dueDate = formatWrittenDayMonthYear(moment.utc().startOf("day").add(props.formState.dueInDays, "days"));
  const dueInText = props.formState.dueInDays === 1
    ? `${RESX.DateTime.lblIn} ${RESX.DateTime.YDay}`
    : `${RESX.DateTime.lblIn} ${props.formState.dueInDays} ${RESX.DateTime.days}`;

  const currency = serverReference.currencies.find((c) => c.value === props.formState.currency);
  const hasOnlyOneBrand = props.brands.length === 1;
  const hasDocumentNumber = props.document.hasDocumentNumber;


  const getUpdateEsignDocumentDetailsInstruction = (documentInfo: EsignDocumentInfo): server.dto.UpdateESignDocumentDetailsInstruction => ({
    documentId: props.document.documentId,
    bookkeepingId: documentInfo.bookkeeping.bookkeepingId,
    brandId: documentInfo.brand.brandId,
    currency: documentInfo.currency,
    exchangeRate: documentInfo.exchangeRate,
    dueInDays: documentInfo.dueInDays,
    ownerUserId: documentInfo.owner.userId,
    allowAutoSign: documentInfo.allowAutoSign,
  });

  return {
    key: "esignDetails",
    type: "regular",
    title: `${site.currentEnvironment[props.documentType].singular} ${RESX.ESign.eSign}`,
    subTitle: `${RESX.ESign.Version} ${props.document.version}`,
    supportArticleId: serverReference.supportArticleIds.whatIsESigning,
    fields: [{
      key: "lifecycleSteps",
      label: "",
      hideLabel: true,
      compact: true,
      render: () => <LifecycleSteps
        documentType={props.documentType}
        stepOptions={{
          draft: { disabled: false },
          review: { disabled: false, validate: props.onReviewClickValidate, hasErrors: props.hasErrors },
          pending: { disabled: true },
          completed: { disabled: true }
        }}
        overDue={false}
        activeStep="draft"
        documentId={props.document.documentId}
      />
    }, SectionTemplateBrField("br"), {
      key: "documentNumber",
      label: RESX.Document.DocumentNumber,
      hidden: !hasDocumentNumber,
      render: () => <div>{props.document.documentNumber}</div>
    }, {
      key: "bookkeeping",
      label: RESX.Bookkeeping.resxBookkeeping,
      editDisabled: hasDocumentNumber,
      render: () => <BookkeepingSmallDetails bookkeeping={props.formState.bookkeeping} />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BookkeepingSmall>) => <BookkeepingPaginatedDropdown
          value={value}
          onChange={(bk) => {
            onChange(bk, true);
          }}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true,
          }}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: props.formState.bookkeeping,
        submitChange: async (bookkeeping: server.dto.BookkeepingSmall) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          bookkeepingId: bookkeeping.bookkeepingId,
          currency: bookkeeping.baseCurrency,
          exchangeRate: 1,
        })
      },
    }, {
      key: "currencyExchangeRate",
      label: RESX.Financial.Currency,
      render: () => <CurrencyAndExchangeRate
        bookkeeping={props.formState.bookkeeping}
        currency={currency}
        exchangeRate={props.formState.exchangeRate}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<ICurrencyAndExchangeRate>) => <CurrencyAndExchangeRatePicker
          focusOnMount
          validatorKey="currencyAndExchangeRate"
          bookkeeping={props.formState.bookkeeping}
          currency={value.currency}
          exchangeRate={value.exchangeRate}
          onChange={onChange}
          onEsc={() => cancel()}
          onEnter={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: { currency: props.formState.currency, exchangeRate: props.formState.exchangeRate },
        submitChange: async (values: ICurrencyAndExchangeRate) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          currency: values.currency,
          exchangeRate: values.exchangeRate
        }),
      },
    }, {
      key: "brand",
      label: RESX.Branding.resxBranding,
      hidden: hasOnlyOneBrand,
      render: () => <div>{props.formState.brand?.name}</div>,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.BrandSmall>) => <BrandDropdown
          options={props.brands}
          value={value}
          onChange={(brand) => {
            onChange(brand, true);
          }}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true,
          }}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: props.formState.brand,
        submitChange: async (brand: server.dto.BrandSmall) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          brandId: brand.brandId,
        })
      },
    }, {
      key: "dueInDays",
      label: RESX.DateTime.Due,
      render: () => <SmallDetails
        label={dueDate}
        subLabel={dueInText}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <>
          <RequiredNumberInput
            autoFocus
            width={60}
            validatorKey="dueInDays"
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
            labelContent={RESX.DateTime.days}
            minValue={0}
            maxValue={999}
          />
          <span style={{ marginLeft: 8 }}>{formatWrittenDayMonthYear(moment.utc().startOf("day").add(value, "days"))}</span>
        </>,
        type: "confirmOrCancelButton",
        initialValue: props.formState.dueInDays,
        submitChange: (dueInDays: number) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          dueInDays,
        })
      },
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={props.formState.owner} />,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
          <UserModalPicker
            inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
            canCreateNew={false}
          />
        </div>,
        fullWidth: false,
        type: "custom",
        initialValue: props.formState.owner,
        submitChange: async (owner: server.dto.UserSmall) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          ownerUserId: owner.userId,
        }),
      },
    }, {
      key: "allowAutoSign",
      label: RESX.ESign.AllowAutoSign,
      supportArticleId: serverReference.supportArticleIds.autoSign,
      render: () => <YesNoText value={props.formState.allowAutoSign} />,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: props.formState.allowAutoSign,
        submitChange: (allowAutoSign: boolean) => window.CoreApi.ESign.UpdateDocumentDetails({
          ...getUpdateEsignDocumentDetailsInstruction(props.formState),
          allowAutoSign,
        }),
      }
    }, {
      key: "inDraftSince",
      label: RESX.DocumentLifeCycle.InDraft,
      render: () => <SmallDetails
        label={formatWrittenDayMonthYear(props.document.sendDate)}
        subLabel={formatDaysSince(props.document.sendDate)}
      />
    }]
  }
}